import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import Gradient from 'modules/post/gradient.component';
import { useSelector } from 'react-redux';
import { ActivityIndicator } from 'react-native-web';
import { Row, View, Image, Pressable } from 'modules/styled';
import { getTitle, getFavIcon } from 'app/utils/post';
import PostInfoMobile from 'modules/post/postinfo.mobile.component';
import { POST_IMAGE_W, POST_IMAGE_H } from 'styles/layout';
import Avatar from 'modules/user/UAvatar.component';
import { CommentBody } from 'modules/comment/commentBody';
import PostTitle from './postTitle.component';
import { resizeImage } from 'utils/img';
import { ImagePost } from './ImagePost';
import { usePostUrl } from './hooks';
import history from 'modules/navigation/history';

PostInfo.propTypes = {
  noLink: PropTypes.bool,
  link: PropTypes.object,
  post: PropTypes.object,
  singlePost: PropTypes.bool,
  preview: PropTypes.bool,
};

export default memo(PostInfo);

function PostInfo({ post, link, noLink, singlePost, preview }) {
  const [showImage, setShowImage] = useState(true);
  const [showFavIcon, setShowFavIcon] = useState(true);
  const screenSize = useSelector((state) => state.navigation.screenSize);
  const postUrl = usePostUrl(post);

  if (post.loading) return <ActivityIndicator />;
  if (!post) return null;

  const imageUrl = resizeImage(link?.image, POST_IMAGE_W, POST_IMAGE_H);
  const favIcon = link?.domain && getFavIcon(link.domain);
  const title = getTitle({ post, link });

  if (post.type == 'image' && link) {
    return (
      <View
        sx={{
          flex: 1,
          flexDirection: singlePost ? 'column' : ['column', 'row'],
        }}
      >
        <ImagePost
          singlePost={singlePost}
          preview={preview}
          url={post.url}
          disabled={noLink}
          postUrl={postUrl}
          _id={post._id}
        />
        {!singlePost ? <View sx={{ mr: 2 }} /> : null}
        {!preview ? (
          <View
            webContainerSx={{ justifyContent: 'flex-end' }}
            sx={{ px: [2, 0], pt: 1, justifyContent: 'flex-end' }}
          >
            <PostTitle
              post={post}
              link={link}
              noLink={noLink}
              singlePost={singlePost}
              preview={preview}
            />
          </View>
        ) : null}
      </View>
    );
  }

  if (screenSize)
    return (
      <PostInfoMobile
        preview={preview}
        post={post}
        singlePost={singlePost}
        noLink={noLink}
        link={link}
      />
    );

  if (link) {
    return (
      <View sx={{ flex: 1, flexDirection: ['column', 'row'] }}>
        <Pressable
          webAction={() => history.push(postUrl)}
          external
          to={post.url}
          target="_blank"
          disabled={noLink}
        >
          <View
            sx={{
              flex: 1,
              width: POST_IMAGE_W * 8,
              height: POST_IMAGE_H * 8,
              mr: 2,
            }}
          >
            {showImage && imageUrl ? (
              <Image
                sx={{ flex: 1, width: POST_IMAGE_W * 8, height: POST_IMAGE_H * 8 }}
                source={{ uri: imageUrl }}
                onError={() => setShowImage(false)}
              />
            ) : showFavIcon && favIcon ? (
              <Image
                resizeMode="cover"
                sx={{ flex: 1, width: POST_IMAGE_W * 8, height: POST_IMAGE_H * 8 }}
                source={{ uri: favIcon }}
                onError={() => setShowFavIcon(false)}
              />
            ) : (
              <Gradient flex={1} title={title} />
            )}
          </View>
        </Pressable>
        <PostTitle
          post={post}
          link={link}
          noLink={noLink}
          title={title}
          singlePost={singlePost}
          preview={preview}
        />
      </View>
    );
  }

  return (
    <Row sx={{ flex: 1, p: 2, alignItems: 'center' }}>
      <Avatar size={6} user={post.embeddedUser} noLink />
      <View sx={{ mr: 2 }} />
      <CommentBody noLink={true} comment={post} preview={true} />
    </Row>
  );
}
